import { useNavigate, useSearch } from '@tanstack/react-router';
import { type SetStateAction, useCallback, useState } from 'react';
import { z } from 'zod';

/**
 * Page and sort as URL search params, for the search routes that declare them. Add these to a
 * route's `validateSearch`; a route that doesn't declare them strips them, so only pass
 * `syncToUrl` where they're declared.
 */
export const pageAndSortSearchShape = {
  page: z.coerce.number().int().min(0).optional().catch(undefined),
  sort: z.string().optional().catch(undefined),
};

/** A sort as a table column id and direction, written to the URL as `column.asc`. */
export type ListSort = { id: string; desc: boolean };

export const parseListSort = (value: string | undefined): ListSort | null => {
  if (!value) return null;
  const [id, direction] = value.split('.');
  if (!id) return null;
  return { id, desc: direction === 'desc' };
};

export const formatListSort = (sort: ListSort) => `${sort.id}.${sort.desc ? 'desc' : 'asc'}`;

/**
 * Filter values compared by what they ask for, so '' and undefined (one from the defaults, one
 * from the URL) don't count as a change. Used to reset the page only when the filters really
 * changed, not when the form first loads from the URL.
 */
export const filtersKey = (values: Record<string, unknown>) =>
  JSON.stringify(
    Object.entries(values)
      .filter(
        ([, value]) => value !== '' && value !== undefined && value !== null && value !== false,
      )
      .sort(([first], [second]) => first.localeCompare(second)),
  );

/**
 * The page and sort of a search list: in the URL when `syncToUrl`, so a reload or a shared link
 * keeps them; otherwise local, as in a picker, which must not write to its host page's URL.
 * Changing the sort goes back to the first page.
 */
export const useListPageAndSort = ({ syncToUrl }: { syncToUrl: boolean }) => {
  const search = useSearch({ strict: false }) as { page?: number; sort?: string };
  const navigate = useNavigate();
  const [localPage, setLocalPage] = useState(0);
  const [localSort, setLocalSort] = useState<string | undefined>(undefined);

  const writeSearch = useCallback(
    (
      patch: (previous: { page?: number; sort?: string }) => { page?: number; sort?: string },
      replace = false,
    ) =>
      navigate({
        to: '.',
        search: ((previous: Record<string, unknown>) => ({
          ...previous,
          ...patch(previous as { page?: number; sort?: string }),
        })) as never,
        replace,
      }),
    [navigate],
  );

  const setPage = useCallback(
    (next: SetStateAction<number>) => {
      if (!syncToUrl) {
        setLocalPage(next);
        return;
      }
      // Going back to the first page (what a filter change does) replaces the history entry, so
      // Back returns to the previous search rather than to a reset in between.
      const isReset = typeof next === 'number' && next <= 0;
      void writeSearch((previous) => {
        const resolved = typeof next === 'function' ? next(previous.page ?? 0) : next;
        // Page 0 is the default, so it isn't written.
        return { page: resolved > 0 ? resolved : undefined };
      }, isReset);
    },
    [syncToUrl, writeSearch],
  );

  const setSort = useCallback(
    (sort: ListSort | null) => {
      const value = sort ? formatListSort(sort) : undefined;
      if (!syncToUrl) {
        setLocalSort(value);
        setLocalPage(0);
        return;
      }
      void writeSearch(() => ({ sort: value, page: undefined }));
    },
    [syncToUrl, writeSearch],
  );

  return {
    page: syncToUrl ? search.page ?? 0 : localPage,
    setPage,
    sort: parseListSort(syncToUrl ? search.sort : localSort),
    setSort,
  };
};
